import { Controller } from "@hotwired/stimulus"

// Obsługa passkeys (WebAuthn) — rejestracja w ustawieniach i logowanie na kroku 2FA.
//
// Targety/values przekazywane z ERB:
//   - data-account--webauthn-options-value       — opcje ceremonii get() wyrenderowane serwerowo (logowanie)
//   - data-account--webauthn-options-url-value   — endpoint zwracający opcje create() (rejestracja)
//   - data-account--webauthn-verify-url-value    — endpoint weryfikacji (create=zapis / get=login)
//   - data-account--webauthn-mode-value          — "register" | "authenticate"
//   - data-account--webauthn-target="error"      — kontener na komunikat błędu (opcjonalny)
//   - data-account--webauthn-target="nickname"   — input z nazwą klucza (rejestracja, opcjonalny)
export default class extends Controller {
  static values = {
    options: Object,
    optionsUrl: String,
    verifyUrl: String,
    mode: String,
    testOptionsUrl: String,
    testVerifyUrl: String,
    testCancelledMessage: String,
    testSuccessMessage: String,
    failedMessage: String,
    sendCodeUrl: String,
    codeSentUrl: String,
    // stan odroczony (S5): kod nie został wysłany — po świadomej odmowie passkeya wysyłamy go sami
    codeDeferred: Boolean,
  }

  static targets = ["error", "nickname", "submit", "result", "passkeyItem", "passkeyUi", "codeForm", "backupCodeToggle", "codeInput", "sendCodeButton"]
  static classes = ["resultSuccess", "resultFailure", "matched"]

  // Fallback (ms) dla przeglądarek, które nie tracą focusu na czas natywnego dialogu
  // (część WebView, Android Chrome bottom sheet) — tam o „user widział dialog" wnioskujemy
  // z czasu: odmowa z braku gestu (macOS Safari) przychodzi natychmiast. Sygnałem głównym
  // jest obserwacja dialogu, patrz rejectedDeliberately.
  static GESTURELESS_REJECT_THRESHOLD_MS = 500

  async connect() {
    if (this.modeValue !== "authenticate") return

    if (!window.PublicKeyCredential) {
      // Brak WebAuthn → ukryj UI klucza; zostaje przycisk „Wyślij kod" (S5)
      // albo widoczne pole kodu (pozostałe scenariusze).
      this.hidePasskey()
      return
    }

    // Auto-próba modala po wejściu na stronę. Chrome i iOS Safari 17.4+ odpalą ceremonię
    // bez gestu (sukces → redirect). macOS Safari / starsze iOS wymagają gestu → modal
    // rzuci NotAllowedError natychmiast, łapiemy po cichu i zostaje interfejs dwóch
    // przycisków (Zaloguj kluczem / Wyślij kod). Po świadomym wysłaniu kodu (param
    // code_sent) NIE ponawiamy auto-modala — user wybrał kod, nie naprzykrzamy się kluczem.
    if (this.autoModalSuppressed) return
    try {
      await this.getCredential()
    } catch (e) {
      // Świadoma odmowa (user widział dialog) w stanie odroczonym → wysyłamy kod od razu,
      // bez kazania klikać „Wyślij kod". Odrzut bez gestu: UI przycisków zostaje, bez krzyku.
      if (e.deliberateReject) {
        await this.autoSendDeferredCode()
      } else if (e.name !== "NotAllowedError" && e.name !== "AbortError") {
        // Awaria inna niż odmowa (np. SecurityError przy niezgodnym rp_id) — bez komunikatu
        // user nie ma jak się dowiedzieć, czemu klucz nie zadziałał.
        this.showError(e.message)
      }
    }
  }

  // Czy odmowa ceremonii była świadoma (user widział dialog i go zamknął)? WebAuthn celowo
  // zwija anulowanie, timeout, brak pasującego klucza i odmowę bez gestu do jednego
  // nieprzejrzystego NotAllowedError, więc rozstrzygamy obserwacją, nie treścią błędu:
  //   - strona straciła focus na czas ceremonii → dialog się pokazał, odmowa jest świadoma,
  //   - ceremonia ruszyła z gestu (klik) → przeglądarka nie miała powodu odmówić bez dialogu,
  //   - inaczej fallback czasowy (patrz GESTURELESS_REJECT_THRESHOLD_MS).
  rejectedDeliberately(e, ceremony) {
    if (e.name !== "NotAllowedError" && e.name !== "AbortError") return false
    if (ceremony.userGesture || ceremony.dialogShown) return true
    return Date.now() - ceremony.startedAt >= this.constructor.GESTURELESS_REJECT_THRESHOLD_MS
  }

  // Obserwacja, czy natywny dialog przejął ekran: strona traci wtedy focus. Odmowa
  // przeglądarki z braku gestu przychodzi zanim cokolwiek się pokaże, więc bez bluru.
  startCeremony(userGesture) {
    const ceremony = { startedAt: Date.now(), dialogShown: false, userGesture }
    ceremony.onBlur = () => { ceremony.dialogShown = true }
    // Tylko przejście w tło; powrót na kartę (hidden → visible) nie dowodzi niczego o dialogu.
    ceremony.onVisibility = () => { if (document.visibilityState === "hidden") ceremony.onBlur() }
    window.addEventListener("blur", ceremony.onBlur, { once: true })
    document.addEventListener("visibilitychange", ceremony.onVisibility)
    return ceremony
  }

  endCeremony(ceremony) {
    window.removeEventListener("blur", ceremony.onBlur)
    document.removeEventListener("visibilitychange", ceremony.onVisibility)
  }

  // Automatyczna wysyłka kodu po świadomej odmowie passkeya — tylko stan odroczony (S5):
  // kod jeszcze nie istnieje, a user właśnie pokazał, że kluczem się nie zaloguje.
  async autoSendDeferredCode() {
    if (!this.codeDeferredValue) return
    await this.deliverCode(this.hasSendCodeButtonTarget ? this.sendCodeButtonTarget : null)
  }

  // Czy pomijamy auto-modal (bo user właśnie świadomie wysłał kod)?
  get autoModalSuppressed() {
    return new URLSearchParams(window.location.search).has("code_sent")
  }

  hidePasskey() {
    if (this.hasPasskeyUiTarget) this.passkeyUiTarget.classList.add("hidden")
  }

  // Klik „Zaloguj kluczem dostępu" — gest usera, więc modal działa też na Safari.
  authenticate(event) {
    event?.preventDefault()
    this.run(async () => {
      try {
        await this.getCredential(true)
      } catch (e) {
        // Świadoma odmowa (anulowanie/timeout) w stanie odroczonym → wysyłamy kod od razu
        // zamiast wracać do przycisków.
        if (e.deliberateReject && this.codeDeferredValue) {
          await this.autoSendDeferredCode()
          this.disableSubmit(false) // wysyłka nie wyszła (błąd pokazany) — odblokuj przycisk klucza
          return
        }
        throw e
      }
    })
  }

  // Klik „Wyślij kod SMS/e-mail" (S5). No-JS: button_to robi zwykły POST → redirect.
  async sendCode(event) {
    event?.preventDefault()
    await this.deliverCode(event?.currentTarget)
  }

  // Wysyłka kodu (AJAX) — z przycisku „Wyślij kod" albo automatycznie po odmowie passkeya.
  // Sukces → przejście na stronę z kodem (param code_sent ucisza auto-modal), gdzie serwer
  // renderuje pole kodu i flash „kod wysłany". sended falsy — false LUB null (np. brak ważnego
  // telefonu albo metoda wyłączona dla apki: send_sms zwraca nil) → pokazujemy błąd i zostajemy
  // (kod nie poszedł, pola nie ma czym wypełnić). Disable dotyczy przycisku wysyłki, nie passkey.
  async deliverCode(button) {
    this.clearError()
    button?.setAttribute("disabled", "disabled")
    try {
      const result = await this.postJson(this.sendCodeUrlValue, {})
      // !result.sended (nie ===false): serwer może zwrócić sended:null (send_sms → nil), którego
      // ===false by nie złapał i przeszedłby na stronę kodu mimo że kod nie wyszedł.
      if (!result.sended) {
        this.showError(result.message)
        // Rate limit (cooldown_seconds > 0, np. kod wysłany w innej zakładce) — button
        // zostaje disabled do końca cooldownu. Inne błędy: odblokowanie od razu.
        if (result.cooldown_seconds > 0) {
          setTimeout(() => button?.removeAttribute("disabled"), result.cooldown_seconds * 1000)
        } else {
          button?.removeAttribute("disabled")
        }
      } else {
        window.location = this.codeSentUrlValue
      }
    } catch (e) {
      this.showError(e.message)
      button?.removeAttribute("disabled")
    }
  }

  // Klik „Użyj kodu zapasowego" (S5b) — odsłania schowane pole kodu (przyjmie kod zapasowy)
  // i chowa sam przycisk (pole już widoczne, nie ma czego odsłaniać drugi raz).
  revealBackupCode(event) {
    event?.preventDefault()
    if (this.hasBackupCodeToggleTarget) this.backupCodeToggleTarget.classList.add("hidden")
    if (this.hasCodeFormTarget) this.codeFormTarget.classList.remove("hidden")
    if (this.hasCodeInputTarget) this.codeInputTarget.focus()
  }

  register(event) {
    event?.preventDefault()
    this.run(() => this.createCredential())
  }

  // Test logowania kluczem w bieżącej przeglądarce (ustawienia). Pełna ceremonia
  // get(), weryfikacja na serwerze, wynik pokazany inline — bez przekierowania.
  async test(event) {
    event?.preventDefault()
    this.clearResult()
    this.clearHighlights()
    const button = event?.currentTarget
    button?.setAttribute("disabled", "disabled")
    try {
      const options = await this.postJson(this.testOptionsUrlValue, {})
      // Jak w getCredential: odpowiedź bez challenge (błąd / redirect) nie może trafić do
      // dekodera — bufferDecode(undefined) wybuchałby TypeErrorem zamiast komunikatu.
      if (options.redirect_url) {
        window.location = options.redirect_url
        return
      }
      if (options.error || !options.challenge) {
        throw new Error(options.error || this.testCancelledMessageValue)
      }
      this.decodeAssertionOptions(options)

      const credential = await navigator.credentials.get({ publicKey: options })
      // Sukces zwraca passkey_json (z id); błąd → { error }. Zaznaczamy zmatchowany
      // klucz po id (odporne na escapowanie nazwy), komunikat jest generyczny.
      const result = await this.postJson(this.testVerifyUrlValue, {
        passkey: this.assertionToJson(credential),
      })

      if (result.error || !result.id) {
        this.showResult(false, result.error || this.testCancelledMessageValue)
      } else {
        this.highlightPasskey(result.id)
        this.showResult(true, this.testSuccessMessageValue)
      }
    } catch (e) {
      // NotAllowedError/AbortError są niejednoznaczne (anulowanie vs brak klucza)
      // — pokazujemy neutralny komunikat zamiast krzyczeć błędem.
      const message = e.name === "NotAllowedError" || e.name === "AbortError"
        ? this.testCancelledMessageValue
        : e.message
      this.showResult(false, message)
    } finally {
      button?.removeAttribute("disabled")
    }
  }

  // Usunięcie passkeya (data-url + opcjonalny data-confirm na przycisku).
  remove(event) {
    event.preventDefault()
    const { url, confirm } = event.currentTarget.dataset
    if (confirm && !window.confirm(confirm)) return
    this.run(async () => {
      const result = await this.postJson(url, {}, "DELETE")
      // Backend może zażądać świeżego 2FA (fresh_required) — przekieruj na re-auth.
      if (result.redirect_url) {
        window.location = result.redirect_url
        return
      }
      if (result.error) throw new Error(result.error)
      window.location.reload()
    })
  }

  // --- Logowanie ---

  // userGesture — czy ceremonia rusza z kliku usera (wpływa na klasyfikację odmowy).
  async getCredential(userGesture = false) {
    // Opcje przychodzą z serwera razem ze stroną, więc get() startuje w tym samym tasku
    // co gest usera i nic nie dzieje się „przed dialogiem". Świeża kopia per ceremonia —
    // decodeAssertionOptions podmienia pola na bufory i drugie wywołanie by je zepsuło.
    const options = this.decodeAssertionOptions(this.optionsValue)
    if (!options.challenge) throw new Error(this.failedMessageValue || "passkey unavailable")

    const ceremony = this.startCeremony(userGesture)
    let credential
    try {
      credential = await navigator.credentials.get({ publicKey: options })
    } catch (e) {
      // Klasyfikacja powstaje tutaj, bo tylko tu znamy ceremonię, której dotyczy błąd.
      // Wywołujący decyduje, co z nią zrobić.
      e.deliberateReject = this.rejectedDeliberately(e, ceremony)
      throw e
    } finally {
      this.endCeremony(ceremony)
    }
    // remember_me jak przy logowaniu kodem — czytamy ten sam checkbox (gdy ukryty:
    // refresh_2fa/podejrzana sesja → brak → false), żeby passkey zapamiętywał urządzenie
    // tak samo jak kod. Serwer interpretuje to przez remember_device?.
    const result = await this.postJson(this.verifyUrlValue, {
      passkey: this.assertionToJson(credential),
      verification: { remember_me: this.rememberMe() },
    })

    if (result.redirect_url) {
      window.location = result.redirect_url
    } else {
      throw new Error(result.error || "verification failed")
    }
  }

  // --- Rejestracja ---

  async createCredential() {
    const options = await this.postJson(this.optionsUrlValue, {})
    // Backend może zażądać najpierw kodów zapasowych (lub świeżego 2FA) — wtedy przekieruj.
    if (options.redirect_url) {
      window.location = options.redirect_url
      return
    }
    if (options.error) throw new Error(options.error)
    this.decodeCreationOptions(options)

    const credential = await navigator.credentials.create({ publicKey: options })
    const nickname = this.hasNicknameTarget ? this.nicknameTarget.value : null
    const result = await this.postJson(this.verifyUrlValue, {
      passkey: this.attestationToJson(credential),
      nickname,
    })

    // Sukces zwraca passkey_json (z id); błąd → { error }.
    if (result.error || !result.id) throw new Error(result.error || "registration failed")
    // Przeładuj, żeby zobaczyć nowy klucz (i ewentualne kody zapasowe).
    window.location.reload()
  }

  // --- Helpers ---

  async run(fn) {
    this.clearError()
    this.disableSubmit(true)
    try {
      await fn()
    } catch (e) {
      // Anulowanie przez usera (NotAllowedError) nie jest błędem do pokazania krzykiem.
      if (e.name !== "NotAllowedError" && e.name !== "AbortError") {
        this.showError(e.message)
      }
      this.disableSubmit(false)
    }
  }

  async postJson(url, body, method = "POST") {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token,
      },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))
    // fetch podąża za 3xx automatycznie; jeśli serwer przekierował (np. brak oczekującej
    // weryfikacji 2FA — user już zalogowany), oddaj docelowy URL zamiast pustego body.
    if (response.redirected && !data.redirect_url) data.redirect_url = response.url
    if (!response.ok && !data.error) {
      throw new Error("request failed")
    }
    return data
  }

  // Stan checkboxa "zapamiętaj urządzenie" z formularza kodu (ten sam, którego używa
  // logowanie kodem). Gdy ukryty (refresh_2fa / podejrzana sesja) → brak → false.
  rememberMe() {
    const cb = document.querySelector('input[name="verification[remember_me]"]')
    return cb ? cb.checked : false
  }

  assertionToJson(credential) {
    return {
      id: credential.id,
      rawId: this.bufferEncode(credential.rawId),
      type: credential.type,
      clientExtensionResults: credential.getClientExtensionResults(),
      response: {
        authenticatorData: this.bufferEncode(credential.response.authenticatorData),
        clientDataJSON: this.bufferEncode(credential.response.clientDataJSON),
        signature: this.bufferEncode(credential.response.signature),
        userHandle: credential.response.userHandle
          ? this.bufferEncode(credential.response.userHandle)
          : null,
      },
    }
  }

  attestationToJson(credential) {
    const response = credential.response
    return {
      id: credential.id,
      rawId: this.bufferEncode(credential.rawId),
      type: credential.type,
      clientExtensionResults: credential.getClientExtensionResults(),
      response: {
        attestationObject: this.bufferEncode(response.attestationObject),
        clientDataJSON: this.bufferEncode(response.clientDataJSON),
        transports: response.getTransports ? response.getTransports() : [],
      },
    }
  }

  // Dekodowanie opcji z serwera do navigator.credentials.get():
  // challenge i id z allowCredentials przychodzą jako base64url.
  decodeAssertionOptions(options) {
    options.challenge = this.bufferDecode(options.challenge)
    options.allowCredentials = (options.allowCredentials || []).map((c) => ({
      ...c,
      id: this.bufferDecode(c.id),
    }))
    return options
  }

  // Dekodowanie opcji z serwera do navigator.credentials.create().
  // user.id to WebAuthn user handle = surowe user.id (string), bajty UTF-8 — nie base64.
  decodeCreationOptions(options) {
    options.challenge = this.bufferDecode(options.challenge)
    options.user.id = new TextEncoder().encode(options.user.id)
    options.excludeCredentials = (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: this.bufferDecode(c.id),
    }))
    return options
  }

  // base64url -> Uint8Array
  bufferDecode(value) {
    const pad = "=".repeat((4 - (value.length % 4)) % 4)
    const base64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/")
    const raw = window.atob(base64)
    return Uint8Array.from(raw, (c) => c.charCodeAt(0))
  }

  // ArrayBuffer -> base64url
  bufferEncode(value) {
    const bytes = new Uint8Array(value)
    let str = ""
    bytes.forEach((b) => (str += String.fromCharCode(b)))
    return window.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  // Wynik testu logowania kluczem. Kolory (sukces/porażka) przez static classes
  // z HTML — bez hardcodowania klas w JS.
  showResult(success, message) {
    if (!this.hasResultTarget) return

    this.resultTarget.textContent = message
    this.resultTarget.classList.remove("hidden")
    if (this.hasResultSuccessClass && this.hasResultFailureClass) {
      this.resultTarget.classList.toggle(this.resultSuccessClass, success)
      this.resultTarget.classList.toggle(this.resultFailureClass, !success)
    }
  }

  clearResult() {
    if (!this.hasResultTarget) return

    this.resultTarget.textContent = ""
    this.resultTarget.classList.add("hidden")
  }

  // Zaznacz na liście passkey, który zmatchował test (po id — nie po nazwie).
  highlightPasskey(id) {
    if (!this.hasMatchedClass) return
    this.passkeyItemTargets.forEach((el) => {
      const match = String(el.dataset.passkeyId) === String(id)
      this.matchedClasses.forEach((c) => el.classList.toggle(c, match))
    })
  }

  clearHighlights() {
    if (!this.hasMatchedClass) return
    this.passkeyItemTargets.forEach((el) => el.classList.remove(...this.matchedClasses))
  }

  disableSubmit(disabled) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = disabled
    }
  }
}
